import axios from "axios";

export const getProject = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/projects/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getProjects = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get("/projects", params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export type EarlyPayOffer = {
	invoiceId: string;
	invoiceSequence: number;
	quoteId: string;
	quoteSequence: number;
	projectId: string | null;
	projectName: string | null;
	projectSequence: number | null;
	deadline: string;
	total: number;
	fullPct: number;
	fullAmount: number;
	fullSave: number;
	depositPct: number;
	depositAmount: number;
	depositSave: number;
};

/** Open early-payment offers for the logged-in client (read-only). */
export const getEarlyPayOffers = async (): Promise<EarlyPayOffer[]> => {
	try {
		const res: any = await axios.get("/projects/early-pay-offers");
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getProjectsEligibleForDiscount = async () => {
	try {
		const res: any = await axios.get("/projects/eligible-for-discount");
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getProjectNextStatus = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/projects/${data?.id}/next-status`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getProjectManualStatuses = async () => {	
	try {
		const res: any = await axios.get(`/projects/status/get-manual-statuses`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// All project statuses ordered by stepNumber, for the project-view stage bar.
export const getAllProjectStatuses = async () => {
	try {
		const res: any = await axios.get(`/projects/status/all`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getProjectQuotes = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/projects/${data?.id}/quotes`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getProjectTimeline = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/projects/${data?.id}/timeline`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updateProject = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/projects/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const addProjectPhoto = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/projects/${id}/photos`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const addProjectFile = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/projects/${id}/files`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/**
 * Tell the team what the client just added, as ONE Slack digest
 * (2026-10-12). `keepalive` survives the tab closing / navigating away;
 * best effort, never throws.
 */
export const flushClientInput = (id?: string | null, keepalive = false) => {
	if (!id) return;
	try {
		if (keepalive) {
			const user = JSON.parse(localStorage.getItem("user") || "null");
			const token = user?.tokens?.accessToken;
			const base = (axios.defaults.baseURL || process.env.REACT_APP_BACKEND || "").replace(/\/$/, "");
			void fetch(`${base}/projects/${id}/client-input/flush`, {
				method: "POST",
				keepalive: true,
				headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
				body: "{}",
			}).catch(() => undefined);
		} else {
			void axios.post(`/projects/${id}/client-input/flush`, {}).catch(() => undefined);
		}
	} catch {
		/* best effort */
	}
};

/** Label a project file: kind ('po' | 'site_map' | 'photo' | 'other') + description. */
export const updateProjectFile = async ({ id, fileId, data }: any) => {
	try {
		const res: any = await axios.put(`/projects/${id}/files/${fileId}`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const createProjectQuote = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/projects/${id}/quote`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Price a single service+product line for the project-level new-quote form,
// the same way an Instant Estimate would. Returns
// { source, pricedAt, cost, markup, price, distancePercentage }.
export const priceProjectLine = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/projects/${id}/price-line`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Resolve the depot for a project-level new-quote line the same way an
// Instant Estimate would, from the project's site address (no depot in).
// Returns { source, pricedAt, default, alternatives }. price-line above
// stays as the override call for repricing a specific chosen depot.
export const resolveProjectLine = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/projects/${id}/resolve-line`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getProjectCoverage = async ({ id }: any) => {
	try {
		const res: any = await axios.get(`/projects/${id}/coverage`);
		return res?.data ? res.data : { pairs: [], productIds: [], serviceIds: [] };
	} catch (e: any) {
		return { pairs: [], productIds: [], serviceIds: [] };
	}
};

export const getProjectPhotos = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/projects/${data?.id}/photos`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getProjectFiles = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/projects/${data?.id}/files`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const markPhotoForInvoice = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/projects/${id}/photos/${data.photoId}/on-invoice`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getProjectStatusByName = async ({ data }: any) => {
	
	try {
		const res: any = await axios.post(`/projects/status/get-status-by-name`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const deleteProjectPhoto = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.delete(`/projects/${data?.id}/photos/${data?.photoId}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const deleteProjectFile = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.delete(`/projects/${data?.id}/files/${data?.fileId}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const createDispatch = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/projects/dispatches`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updateDispatch = async ({ id, data }: { id: string; data: any }) => {
	try {
		const res: any = await axios.put(`/projects/dispatches/${id}`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};


/* Driver dispatch PIN (2026-10-16): checked server-side now. The page keeps
 * the PIN for this tab only and sends it on every dispatch call. */
const DISPATCH_PIN_KEY = (id: string) => `dispatch-pin:${id}`;
export const setDispatchPin = (id: string, pin: string | null) => {
	try {
		if (pin) sessionStorage.setItem(DISPATCH_PIN_KEY(id), pin);
		else sessionStorage.removeItem(DISPATCH_PIN_KEY(id));
	} catch {
		/* storage unavailable: the PIN is asked again */
	}
};
export const getDispatchPin = (id: string): string | null => {
	try {
		return sessionStorage.getItem(DISPATCH_PIN_KEY(id));
	} catch {
		return null;
	}
};
const pinHeaders = (id: string, pin?: string | null) => {
	const p = pin ?? getDispatchPin(id);
	return p ? { "x-dispatch-pin": p } : {};
};

export const markDispatchNotesRead = async ({ id }: { id: string }) => {
	try {
		const res: any = await axios.put(
			`/projects/dispatches/by-id/${id}/driver-notes-read`,
			{},
			{ excludeInterceptor: true, headers: pinHeaders(id) } as any,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getDispatchById = async ({ id, pin }: { id: string; pin?: string | null }) => {
    try {
        const res: any = await axios.get(
            `/projects/dispatches/by-id/${id}`,
            { excludeInterceptor: true, headers: pinHeaders(id, pin) } as any,
        );
        return res?.data ? res.data : {};
    } catch (e: any) {
        return e?.response?.data ? e.response.data : e;
    }
};

export const getDispatchByPurchaseOrder = async ({ reference }: { reference: string }) => {
	if (!reference) {
		return null;
	}
	try {
		const res: any = await axios.get(`/projects/dispatches/${encodeURIComponent(reference)}`);
		return res?.data ? res.data : {} ;
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const markDispatchOnTheWay = async ({ id }: { id: string }) => {
	try {
		const res: any = await axios.post(`/projects/dispatches/${id}/on-the-way`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const markDispatchOnSite = async ({ id }: { id: string }) => {
	try {
		const res: any = await axios.put(
			`/projects/dispatches/by-id/${id}/on-site`,
			{},
			{ excludeInterceptor: true, headers: pinHeaders(id) } as any,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const uploadDispatchPhoto = async ({ id, data }: { id: string; data: any }) => {
	try {
		const res: any = await axios.post(
			`/projects/dispatches/by-id/${id}/photos`,
			data,
			{ excludeInterceptor: true, headers: pinHeaders(id) } as any,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const completeDispatch = async ({ id }: { id: string }) => {
	try {
		const res: any = await axios.put(
			`/projects/dispatches/by-id/${id}/complete`,
			{},
			{ excludeInterceptor: true, headers: pinHeaders(id) } as any,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/** Signed upload URL for a driver photo (no login; PIN-checked). */
export const getDispatchPhotoUploadUrl = async ({ id, file }: { id: string; file: File }) => {
	try {
		const res: any = await axios.post(
			`/projects/dispatches/by-id/${id}/photo-upload-url`,
			{ name: file.name, type: file.type, size: file.size },
			{ excludeInterceptor: true, headers: pinHeaders(id) } as any,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/** Share a project file / photo with one provider, or stop sharing. Staff only. */
export const shareProjectAsset = async ({ projectId, kind, assetId, providerId }: { projectId: string; kind: "files" | "photos"; assetId: string; providerId: string | null }) => {
	try {
		const res: any = await axios.put(`/projects/${projectId}/${kind}/${assetId}/share`, { providerId });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};
